import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
// components
import { ExternalRedirectionComponent } from './core/components/external-redirection/external-redirection.component';
// constants
import { BASE_URL_PATH, GROUP_BASE_URL_PATH, GROUP_LOCATIONS_BASE_URL_PATH } from './core/constants';
// guards
import { CustomerGuard } from './core/guards/customer.guard';
import { CheckoutGuard } from './core/guards/checkout.guard';
import { CloseTabGuard } from './core/guards/closeTab.guard';
import { VerifiedCustomerGuard } from './pages/restaurant/guards/verified-customer.guard';
import {TableRedirectService} from './core/services/TableRedirectService';

const routes: Routes = [
  {
    path: `g/tabs/:tabId`,
    loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsModule),
  },
  {
    path: `tabs/:tabId`,
    loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsModule),
  },
  {
    path: `delivery/:transactionId/cancel`,
    loadChildren: () => import('./pages/cancel-delivery/cancel-delivery.module').then((m) => m.CancelDeliveryModule),
  },
  {
    path: `sms/:transactionId`,
    loadChildren: () => import('./pages/restaurant/restaurant.module').then(m => m.RestaurantModule)
  },
  {
    path: `${BASE_URL_PATH}/:merchantId`,
    loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
  },
  {
    path: `locations/${GROUP_BASE_URL_PATH}/:groupId`,
    loadChildren: () => import('./pages/home-group/home-group.module').then((m) => m.HomeGroupModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations`,
    redirectTo: `${BASE_URL_PATH}/:merchantId`,
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId`,
    loadChildren: () => import('./pages/restaurant/restaurant.module').then((m) => m.RestaurantModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/tables/:tableNumber`,
    loadChildren: () => import('./pages/restaurant/restaurant.module').then((m) => m.RestaurantModule),
    canActivate: [TableRedirectService]
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/orders`,
    loadChildren: () => import('./pages/orders-status/orders-status.module').then((m) => m.OrdersStatusModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/cart`,
    loadChildren: () => import('./pages/cart/cart.module').then((m) => m.CartModule),
  },
  {
    path: `${GROUP_BASE_URL_PATH}/:groupId`,
    loadChildren: () => import('./pages/restaurant-group/restaurant-group.module').then((m) => m.RestaurantGroupModule),
  },
  {
    path: `${GROUP_BASE_URL_PATH}/:groupId/tables/:tableNumber`,
    loadChildren: () => import('./pages/restaurant-group/restaurant-group.module').then((m) => m.RestaurantGroupModule),
  },
  {
    path: `${GROUP_LOCATIONS_BASE_URL_PATH}/:groupId`,
    loadChildren: () => import('./pages/restaurant-group/restaurant-group.module').then((m) => m.RestaurantGroupModule),
  },
  {
    path: `${GROUP_LOCATIONS_BASE_URL_PATH}/:groupId/tables/:tableNumber`,
    loadChildren: () => import('./pages/restaurant-group/restaurant-group.module').then((m) => m.RestaurantGroupModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/close-tab`,
    loadChildren: () => import('./pages/close-tab/close-tab.module').then((m) => m.CloseTabPageModule),
    canActivate: [CloseTabGuard],
  },
  {
    path: `${GROUP_BASE_URL_PATH}/:groupId/close-tab`,
    loadChildren: () => import('./pages/close-tab/close-tab.module').then((m) => m.CloseTabPageModule),
    canActivate: [CloseTabGuard],
  },
  {
    path: `${GROUP_BASE_URL_PATH}/:groupId/checkout`,
    loadChildren: () => import('./pages/checkout/checkout.module').then((m) => m.CheckoutPageModule),
    canActivate: [CheckoutGuard],
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/checkout`,
    loadChildren: () => import('./pages/checkout/checkout.module').then((m) => m.CheckoutPageModule),
    canActivate: [CheckoutGuard],
  },
  {
    path: `${GROUP_BASE_URL_PATH}/:groupId/order-complete`,
    loadChildren: () => import('./pages/order-complete/order-complete.module').then((m) => m.OrderCompletePageModule),
    canActivate: [CustomerGuard],
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/order-complete`,
    loadChildren: () => import('./pages/order-complete/order-complete.module').then((m) => m.OrderCompletePageModule),
    canActivate: [CustomerGuard],
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/locations/:locationId/order-complete/order-confirmation`,
    loadChildren: () => import('./pages/order-complete/order-complete.module').then((m) => m.OrderCompletePageModule),
    canActivate: [CustomerGuard],
  },
  {
    path: `${BASE_URL_PATH}`,
    loadChildren: () => import('./pages/find-merchant/find-merchant.module').then((m) => m.FindMerchantModule),
  },
  {
    path: `find-merchant`,
    loadChildren: () => import('./pages/find-merchant/find-merchant.module').then((m) => m.FindMerchantModule),
  },
  {
    path: 'find-group',
    loadChildren: () => import('./pages/find-group/find-group.module').then((m) => m.FindGroupModule)
  },
  {
    path: `${BASE_URL_PATH}/:merchantId`,
    loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/rewards`,
    loadChildren: () => import('./pages/rewards/rewards.module').then((m) => m.RewardsModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/order-history`,
    loadChildren: () => import('./pages/order-history/order-history.module').then((m) => m.OrderHistoryModule),
  },
  {
    path: `${BASE_URL_PATH}/:merchantId/gift-card-scan`,
    loadChildren: () => import('./pages/gift-card-scan/gift-card-scan.module').then((m) => m.GiftCardScanModule),
    canActivate: [VerifiedCustomerGuard],
  },
  {
    path: `locations/:locationId/order-ready`,
    loadChildren: () => import('./pages/current-orders/current-orders.module').then((m) => m.CurrentOrdersModule),
  },
  {
    path: `groups/:groupId/order-ready`,
    loadChildren: () => import('./pages/current-orders/current-orders.module').then((m) => m.CurrentOrdersModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
